import Phaser from 'phaser';
import { getDefaultStore } from 'jotai/vanilla';
import { userAtom, type User } from '../atoms/user.atom';
import { settingsAtom, type Settings } from '../atoms/settings.atom';
import { gameStatsAtom, createGameStatsAtom, type GameStats } from '../atoms/stats.atom';

/**
 * StatePlugin - Phaser Scene Plugin for state management
 *
 * Provides easy access to Jotai atoms from Phaser scenes.
 * Automatically persists to localStorage.
 *
 * Usage in scenes: this.state.getUser(), this.state.updateStats(), etc.
 */
export class StatePlugin extends Phaser.Plugins.ScenePlugin {
  private store = getDefaultStore();

  constructor(scene: Phaser.Scene, pluginManager: Phaser.Plugins.PluginManager) {
    super(scene, pluginManager, 'state');
  }

  // ==================== User Methods ====================

  /**
   * Get current user data
   */
  getUser(): User {
    return this.store.get(userAtom);
  }

  /**
   * Update user data
   */
  setUser(user: Partial<User>): void {
    const current = this.getUser();
    this.store.set(userAtom, { ...current, ...user });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getUser().isAuthenticated;
  }

  // ==================== Settings Methods ====================

  /**
   * Get all settings
   */
  getSettings(): Settings {
    return this.store.get(settingsAtom);
  }

  /**
   * Update a single setting
   */
  setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
    const current = this.getSettings();
    this.store.set(settingsAtom, { ...current, [key]: value });
  }

  /**
   * Update multiple settings at once
   */
  updateSettings(settings: Partial<Settings>): void {
    const current = this.getSettings();
    this.store.set(settingsAtom, { ...current, ...settings });
  }

  // ==================== Stats Methods ====================

  /**
   * Get stats for the current game
   */
  getStats(): GameStats {
    const gameKey = this.scene?.sys?.settings?.key ?? 'default';
    const atom = createGameStatsAtom(gameKey);
    return this.store.get(atom);
  }

  /**
   * Get stats for all games
   */
  getAllStats(): Record<string, GameStats> {
    return this.store.get(gameStatsAtom);
  }

  /**
   * Update stats for the current game
   */
  updateStats(update: Partial<GameStats>): void {
    const gameKey = this.scene?.sys?.settings?.key ?? 'default';
    const atom = createGameStatsAtom(gameKey);
    const current = this.store.get(atom);

    this.store.set(atom, {
      ...current,
      ...update,
      lastPlayed: Date.now(),
    });
  }

  /**
   * Increment games played counter for current game
   */
  incrementGamesPlayed(): void {
    const stats = this.getStats();
    this.updateStats({ gamesPlayed: stats.gamesPlayed + 1 });
  }

  /**
   * Update high score if the new score is higher
   */
  updateHighScore(score: number): void {
    const stats = this.getStats();
    if (score > stats.highScore) {
      this.updateStats({
        highScore: score,
        totalScore: stats.totalScore + score,
      });
    } else {
      this.updateStats({
        totalScore: stats.totalScore + score,
      });
    }
  }

  /**
   * Add an achievement (if not already earned)
   */
  addAchievement(achievement: string): void {
    const stats = this.getStats();
    if (!stats.achievements.includes(achievement)) {
      this.updateStats({
        achievements: [...stats.achievements, achievement],
      });
    }
  }

  /**
   * Check if an achievement has been earned
   */
  hasAchievement(achievement: string): boolean {
    const stats = this.getStats();
    return stats.achievements.includes(achievement);
  }

  /**
   * Reset stats for the current game
   */
  resetGameStats(): void {
    const gameKey = this.scene?.sys?.settings?.key ?? 'default';
    const allStats = this.store.get(gameStatsAtom);
    const { [gameKey]: _removed, ...rest } = allStats;
    void _removed; // Intentionally discarded
    this.store.set(gameStatsAtom, rest);
  }

  /**
   * Reset all stats for all games
   */
  resetAllStats(): void {
    this.store.set(gameStatsAtom, {});
  }
}
